<template>
	<div v-if="popupActive" :class="['product-modal-overlay', `theme-${currentTheme}`]" @click.self="closeModal">
		<div :class="['product-modal', `theme-${currentTheme}`]">
			<!-- Modal Header -->
			<div class="modal-header" :style="{ background: headerGradient }">
				<div class="header-content">
					<h1 class="modal-title" :style="{ color: headerTextColor }">{{ product.title }}</h1>
					<div class="product-meta" :style="{ color: headerMetaTextColor }">
						<span class="meta-item">
							<strong>EAN:</strong> {{ product.ean }}
						</span>
						<span class="meta-item">
							<strong>Product ID:</strong> {{ product.id }}
						</span>
					</div>
				</div>
				<button class="close-btn" @click="closeModal" :style="{ color: headerTextColor }">
					<font-awesome-icon icon="times" />
				</button>
			</div>

			<!-- Modal Body with Tabs -->
			<div class="modal-body">
				<div class="tabs-navigation">
					<button
						v-for="(tab, index) in tabs"
						:key="index"
						:class="['tab-button', { active: selectedTab === index }]"
						:style="selectedTab === index ? getTabActiveStyle() : {}"
					@mouseenter="onTabHover($event, true)"
					@mouseleave="onTabHover($event, false, index)"
					@click="selectedTab = index"
					>
						<font-awesome-icon :icon="tab.icon" class="mr-2" />
						{{ tab.label }}
					</button>
				</div>

				<!-- Tab Content -->
				<div class="tabs-content">
					<!-- Product Tab -->
					<div v-show="selectedTab === 0" class="tab-pane">
						<div class="info-section">
							<h3 class="section-title">
								<font-awesome-icon icon="box" class="mr-2" />
								Product Informatie
							</h3>
							<product ref="product" :product="product" />
						</div>
					</div>

					<!-- Shipping Tab -->
					<div v-show="selectedTab === 1" class="tab-pane">
						<div class="info-section">
							<h3 class="section-title">
								<font-awesome-icon icon="shipping-fast" class="mr-2" />
								Verzending & Verpakking
							</h3>
							<shipping ref="shipping" :packages="packages" :deliveryOptions="deliveryOptions" :product="product" />
						</div>
					</div>

					<!-- Price Control Tab -->
					<div v-show="selectedTab === 2" class="tab-pane">
						<div class="info-section">
							<h3 class="section-title">
								<font-awesome-icon icon="dollar-sign" class="mr-2" />
								Prijs Controle
							</h3>
							<price-control ref="priceControl" :product="product" />
						</div>
					</div>

					<!-- Calculator Tab -->
					<div v-show="selectedTab === 3" class="tab-pane">
						<div class="info-section">
							<h3 class="section-title">
								<font-awesome-icon icon="calculator" class="mr-2" />
								Winstcalculator
							</h3>
							<calculator ref="calculator" :product="product" />
						</div>
					</div>

					<!-- Overview Tab -->
					<div v-show="selectedTab === 4" class="tab-pane">
						<div class="overview-grid">
							<div class="info-card">
								<div class="card-icon">📦</div>
								<div class="card-content">
									<p class="card-label">Voorraad</p>
									<p class="card-value">{{ product.stock }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">💰</div>
								<div class="card-content">
									<p class="card-label">Verkoopprijs</p>
									<p class="card-value">€ {{ product.price }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">🛒</div>
								<div class="card-content">
									<p class="card-label">Inkoopprijs</p>
									<p class="card-value">€ {{ product.purchase_price || '-' }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">📏</div>
								<div class="card-content">
									<p class="card-label">Gewicht</p>
									<p class="card-value">{{ product.weight || '-' }} g</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">📍</div>
								<div class="card-content">
									<p class="card-label">Locatie</p>
									<p class="card-value">{{ product.location || '-' }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">⚠️</div>
								<div class="card-content">
									<p class="card-label">Minimale Voorraad</p>
									<p class="card-value">{{ product.stock_threshold || '-' }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">📦</div>
								<div class="card-content">
									<p class="card-label">Bundel Hoeveelheid</p>
									<p class="card-value">{{ product.bundle_quantity || '-' }}</p>
								</div>
							</div>

							<div class="info-card">
								<div class="card-icon">🔄</div>
								<div class="card-content">
									<p class="card-label">Sync Status</p>
									<p class="card-value">
										<span v-if="product.is_syncing" class="status-syncing">Bezig...</span>
										<span v-else class="status-ok">✓ Klaar</span>
									</p>
								</div>
							</div>
						</div>

						<div class="info-section mt-5">
							<h3 class="section-title">Productnaam</h3>
							<div class="product-title-full">
								{{ product.title }}
							</div>
						</div>

						<div class="info-section mt-5">
							<h3 class="section-title">Links</h3>
							<div class="links-section">
								<div v-if="product.buy_url" class="link-item">
									<strong>Inkooplink:</strong>
									<a :href="product.buy_url" target="_blank" class="product-link">
										{{ product.buy_url }}
										<font-awesome-icon icon="external-link-alt" class="ml-1" />
									</a>
								</div>
								<div v-if="product.product_page_url" class="link-item">
									<strong>Product Pagina:</strong>
									<a :href="product.product_page_url" target="_blank" class="product-link">
										{{ product.product_page_url }}
										<font-awesome-icon icon="external-link-alt" class="ml-1" />
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Modal Footer -->
			<div class="modal-footer">
				<div class="footer-content">
					<div class="tab-info">
						<p class="text-sm text-gray-600">
							<font-awesome-icon icon="info-circle" class="mr-2" />
							<span v-if="selectedTab === 0">Bewerk basisgegevens van het product</span>
							<span v-else-if="selectedTab === 1">Bewerk verzend- en verpakkinginformatie</span>
							<span v-else-if="selectedTab === 2">Beheer automatische prijscontrole instellingen</span>
							<span v-else-if="selectedTab === 3">Bereken de verwachte winst per product</span>
							<span v-else>Bekijk alle productdetails</span>
						</p>
					</div>
					<div class="action-buttons">
						<vs-button color="danger" type="flat" @click="closeModal">
							<font-awesome-icon icon="times" class="mr-2" />
							Annuleren
						</vs-button>
						<vs-button @click="validateProduct()">
							<font-awesome-icon icon="save" class="mr-2" />
							Opslaan
						</vs-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import product from './product';
import shipping from './shipping';
import priceControl from './priceControl';
import calculator from './calculator';

export default {
	name: 'updatePopup',
	components: {
		product,
		shipping,
		priceControl,
		calculator,
	},
	data() {
		return {
			popupActive: false,
			selectedTab: 0,
			product: {},
			tabs: [
				{ label: 'Product', icon: 'box' },
				{ label: 'Verzending', icon: 'shipping-fast' },
				{ label: 'Prijs Controle', icon: 'dollar-sign' },
				{ label: 'Calculator', icon: 'calculator' },
				{ label: 'Overzicht', icon: 'eye' },
			],
			deliveryOptions: {
				selected: {},
				options: [
					{
						name: 'Voor 23:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-23',
					},
					{
						name: 'Voor 22:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-22',
					},
					{
						name: 'Voor 21:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-21',
					},
					{
						name: 'Voor 20:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-20',
					},
					{
						name: 'Voor 19:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-19',
					},
					{
						name: 'Voor 18:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-18',
					},
					{
						name: 'Voor 17:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-17',
					},
					{
						name: 'Voor 16:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-16',
					},
					{
						name: 'Voor 15:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-15',
					},
					{
						name: 'Voor 14:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-14',
					},
					{
						name: 'Voor 13:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-13',
					},
					{
						name: 'Voor 12:00 besteld op werkdagen, de volgende dag in huis',
						toApi: '24uurs-12',
					},
					{
						name: '1 - 2 dagen',
						toApi: '1-2d',
					},
					{
						name: '2 - 3 dagen',
						toApi: '2-3d',
					},
					{
						name: '3 - 5 dagen',
						toApi: '3-5d',
					},
					{
						name: '4 - 8 dagen',
						toApi: '4-8d',
					},
					{
						name: '1 - 8 dagen',
						toApi: '1-8d',
					},
				],
			}
		}
	},
	computed: {
		...mapGetters({
			packages: 'packages/packages',
			selectedProduct: 'productStore/selectedProduct',
		}),
		currentTheme() {
			return this.$store.state.theme || 'light';
		},
		primaryColor() {
			return this.$store.state.themePrimaryColor || '#2C8DFB';
		},
		headerGradient() {
			const primary = this.primaryColor;
			// Create gradient based on primary color
			return `linear-gradient(135deg, ${primary} 0%, ${this.adjustBrightness(primary, -20)} 100%)`;
		},
		headerTextColor() {
			// Determine if text should be white or dark based on background brightness
			const primary = this.primaryColor;
			const brightness = this.getColorBrightness(primary);
			return brightness < 128 ? '#ffffff' : '#333333';
		},
		headerMetaTextColor() {
			// Meta text with slightly less opacity
			const primary = this.primaryColor;
			const brightness = this.getColorBrightness(primary);
			return brightness < 128 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(51, 51, 51, 0.8)';
		}
	},
	methods: {
		...mapActions({
			getProducts: 'productStore/getProducts',
		}),
		adjustBrightness(color, percent) {
			// Convert hex to RGB
			const num = parseInt(color.replace("#", ""), 16);
			const amt = Math.round(2.55 * percent);
			const R = Math.max(0, Math.min(255, (num >> 16) + amt));
			const G = Math.max(0, Math.min(255, (num >> 8 & 0x00FF) + amt));
			const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
			return "#" + (0x1000000 + (R < 16 ? 0 : 1) * R * 0x10000 +
				(G < 16 ? 0 : 1) * G * 0x100 + (B < 16 ? 0 : 1) * B)
				.toString(16).slice(1);
		},
		getColorBrightness(color) {
			// Convert hex to RGB and calculate brightness
			const hex = color.replace('#', '');
			const r = parseInt(hex.substr(0, 2), 16);
			const g = parseInt(hex.substr(2, 2), 16);
			const b = parseInt(hex.substr(4, 2), 16);
			// Calculate brightness using luminance formula
			return (r * 299 + g * 587 + b * 114) / 1000;
		},
		getTabActiveStyle() {
			// Return inline styles for active tab using primary color
			const primary = this.primaryColor;
			return {
				background: `rgba(${this.hexToRgb(primary).join(',')}, 0.1)`,
				color: primary,
				borderLeftColor: primary
			};
		},
		hexToRgb(hex) {
			// Convert hex to RGB array
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			return result ? [
				parseInt(result[1], 16),
				parseInt(result[2], 16),
				parseInt(result[3], 16)
			] : [0, 0, 0];
		},
		onTabHover(event, isHovering, tabIndex) {
			// Apply hover styles dynamically based on primary color
			if (isHovering) {
				const rgb = this.hexToRgb(this.primaryColor);
				event.target.style.backgroundColor = `rgba(${rgb.join(',')}, 0.05)`;
				event.target.style.color = this.primaryColor;
			} else {
				// Only reset if not the active tab
				if (this.selectedTab !== tabIndex) {
					event.target.style.backgroundColor = '';
					event.target.style.color = '';
				}
			}
		},
		openPopup() {
			const selectedProduct = {
				...this.selectedProduct,
				product_page_url: this.selectedProduct.bol_offer.product_page_url,
			};
			if (selectedProduct.delivery_code) {
				for (let i = 0; i < this.deliveryOptions.options.length; i++) {
					if (this.deliveryOptions.options[i].toApi === selectedProduct.delivery_code) {
						this.deliveryOptions.selected = this.deliveryOptions.options[i];
					}
				}
			}
			this.product = selectedProduct;
			this.selectedTab = 0;
			this.popupActive = true;
			document.body.style.overflow = 'hidden';
		},
		closeModal() {
			this.popupActive = false;
			document.body.style.overflow = 'auto';
		},
		validateProduct() {
			// Always update calculator values to product, regardless of active tab
			if (this.$refs.calculator) {
				this.$refs.calculator.update();
			}

			// Then validate based on active tab
			if (this.selectedTab === 0) {
				this.$refs.product.validate()
			} else if (this.selectedTab === 1) {
				this.$refs.shipping.validate()
			} else if (this.selectedTab === 2) {
				this.$refs.priceControl.validate()
			} else if (this.selectedTab === 3) {
				// Calculator tab - just validate the product with updated values
				this.$refs.product.validate()
			}
		},
	}
}
</script>

<style scoped>
.product-modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 99999;
	animation: fadeIn 0.3s ease;
}

.product-modal-overlay.theme-dark {
	background: rgba(0, 0, 0, 0.7);
}

.product-modal-overlay.theme-semi-dark {
	background: rgba(0, 0, 0, 0.6);
}

@keyframes fadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

.product-modal {
	background: white;
	color: #333;
	border-radius: 12px;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	width: 90%;
	height: 90vh;
	max-width: 1200px;
	display: flex;
	flex-direction: column;
	animation: slideUp 0.3s ease;
	overflow: hidden;
	transition: background 0.3s ease, color 0.3s ease;
}

.product-modal.theme-dark {
	background: #1e1e1e;
	color: #e0e0e0;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.product-modal.theme-semi-dark {
	background: #2a2a2a;
	color: #e0e0e0;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

@keyframes slideUp {
	from {
		transform: translateY(30px);
		opacity: 0;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

/* Header */
.modal-header {
	color: white;
	padding: 24px 32px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.product-modal.theme-dark .modal-header {
	border-bottom-color: rgba(255, 255, 255, 0.05);
}

.product-modal.theme-semi-dark .modal-header {
	border-bottom-color: rgba(255, 255, 255, 0.05);
}

.header-content {
	flex: 1;
	min-width: 0;
	padding-right: 16px;
}

.modal-title {
	margin: 0;
	font-size: 28px;
	font-weight: 700;
	margin-bottom: 12px;
	word-break: break-word;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.product-meta {
	display: flex;
	gap: 24px;
	flex-wrap: wrap;
	font-size: 13px;
	opacity: 0.9;
}

.meta-item {
	display: flex;
	align-items: center;
	gap: 6px;
	background: rgba(255, 255, 255, 0.1);
	padding: 6px 12px;
	border-radius: 6px;
}

.close-btn {
	background: rgba(255, 255, 255, 0.2);
	border: none;
	color: white;
	width: 40px;
	height: 40px;
	border-radius: 8px;
	cursor: pointer;
	font-size: 18px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.close-btn:hover {
	background: rgba(255, 255, 255, 0.3);
	transform: scale(1.1);
}

/* Body */
.modal-body {
	flex: 1;
	display: flex;
	overflow: hidden;
}

.tabs-navigation {
	width: 200px;
	background: #f8f9fa;
	border-right: 1px solid #e9ecef;
	display: flex;
	flex-direction: column;
	padding: 16px 0;
	overflow-y: auto;
	transition: background 0.3s ease, border-color 0.3s ease;
}

.product-modal.theme-dark .tabs-navigation {
	background: #262626;
	border-right-color: #3a3a3a;
}

.product-modal.theme-semi-dark .tabs-navigation {
	background: #323232;
	border-right-color: #404040;
}

.tab-button {
	background: none;
	border: none;
	padding: 12px 20px;
	text-align: left;
	cursor: pointer;
	color: #666;
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
	border-left: 3px solid transparent;
}

.tab-button:hover {
	/* Hover styles are set dynamically via onTabHover() method */
	transition: background 0.2s ease, color 0.2s ease;
}

.tab-button.active {
	/* Colors are set dynamically via inline styles from getTabActiveStyle() */
}

.product-modal.theme-dark .tab-button {
	color: #aaa;
}

.product-modal.theme-dark .tab-button:hover {
	background: rgba(102, 126, 234, 0.15);
	color: #667eea;
}

.product-modal.theme-semi-dark .tab-button {
	color: #aaa;
}

.product-modal.theme-semi-dark .tab-button:hover {
	background: rgba(102, 126, 234, 0.15);
	color: #667eea;
}

.tabs-content {
	flex: 1;
	overflow-y: auto;
	padding: 32px;
	transition: background 0.3s ease, color 0.3s ease;
}

.product-modal.theme-dark .tabs-content {
	background: #1e1e1e;
	color: #e0e0e0;
}

.product-modal.theme-semi-dark .tabs-content {
	background: #2a2a2a;
	color: #e0e0e0;
}

.tab-pane {
	animation: fadeIn 0.2s ease;
}

.info-section {
	margin-bottom: 32px;
}

.info-section:last-child {
	margin-bottom: 0;
}

.section-title {
	font-size: 18px;
	font-weight: 600;
	color: #333;
	margin: 0 0 20px 0;
	display: flex;
	align-items: center;
	gap: 8px;
	padding-bottom: 12px;
	border-bottom: 2px solid #f0f0f0;
	transition: color 0.3s ease, border-color 0.3s ease;
}

.product-modal.theme-dark .section-title {
	color: #e0e0e0;
	border-bottom-color: #3a3a3a;
}

.product-modal.theme-semi-dark .section-title {
	color: #e0e0e0;
	border-bottom-color: #404040;
}

/* Overview Grid */
.overview-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 16px;
	margin-bottom: 32px;
}

.info-card {
	background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
	border: 1px solid #e9ecef;
	border-radius: 8px;
	padding: 16px;
	display: flex;
	align-items: flex-start;
	gap: 12px;
	transition: all 0.2s ease;
}

.info-card:hover {
	border-color: #667eea;
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
	transform: translateY(-2px);
}

.product-modal.theme-dark .info-card {
	background: linear-gradient(135deg, #2a2a2a 0%, #262626 100%);
	border-color: #3a3a3a;
	color: #e0e0e0;
}

.product-modal.theme-dark .info-card:hover {
	border-color: #667eea;
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.product-modal.theme-semi-dark .info-card {
	background: linear-gradient(135deg, #323232 0%, #2e2e2e 100%);
	border-color: #404040;
	color: #e0e0e0;
}

.product-modal.theme-semi-dark .info-card:hover {
	border-color: #667eea;
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.card-icon {
	font-size: 24px;
	min-width: 32px;
	text-align: center;
}

.card-content {
	flex: 1;
}

.card-label {
	font-size: 12px;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin: 0 0 6px 0;
	font-weight: 600;
	transition: color 0.3s ease;
}

.product-modal.theme-dark .card-label {
	color: #888;
}

.product-modal.theme-semi-dark .card-label {
	color: #888;
}

.card-value {
	font-size: 16px;
	font-weight: 700;
	color: #333;
	margin: 0;
	word-break: break-word;
	transition: color 0.3s ease;
}

.product-modal.theme-dark .card-value {
	color: #e0e0e0;
}

.product-modal.theme-semi-dark .card-value {
	color: #e0e0e0;
}

.status-ok {
	color: #28a745;
	font-weight: 600;
}

.status-syncing {
	color: #ffc107;
	font-weight: 600;
}

/* Product Title Section */
.product-title-full {
	background: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #e9ecef;
	font-size: 14px;
	line-height: 1.6;
	color: #333;
	word-break: break-word;
}

body.theme-dark .product-title-full {
	background: #262626;
	border-color: #3a3a3a;
	color: #e0e0e0;
}

body.theme-semi-dark .product-title-full {
	background: #323232;
	border-color: #404040;
	color: #e0e0e0;
}

/* Links Section */
.links-section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.link-item {
	background: #f8f9fa;
	padding: 12px;
	border-radius: 6px;
	border: 1px solid #e9ecef;
	transition: background 0.3s ease, border-color 0.3s ease;
}

.product-modal.theme-dark .link-item {
	background: #262626;
	border-color: #3a3a3a;
	color: #e0e0e0;
}

.product-modal.theme-semi-dark .link-item {
	background: #323232;
	border-color: #404040;
	color: #e0e0e0;
}

.link-item strong {
	display: block;
	font-size: 12px;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 6px;
	transition: color 0.3s ease;
}

.product-modal.theme-dark .link-item strong {
	color: #888;
}

.product-modal.theme-semi-dark .link-item strong {
	color: #888;
}

.product-link {
	color: #667eea;
	text-decoration: none;
	word-break: break-all;
	font-size: 13px;
	transition: color 0.2s ease;
}

.product-link:hover {
	color: #764ba2;
	text-decoration: underline;
}

/* Footer */
.modal-footer {
	background: #f8f9fa;
	border-top: 1px solid #e9ecef;
	padding: 16px 32px;
	min-height: auto;
	transition: background 0.3s ease, border-color 0.3s ease;
}

.product-modal.theme-dark .modal-footer {
	background: #262626;
	border-top-color: #3a3a3a;
}

.product-modal.theme-semi-dark .modal-footer {
	background: #323232;
	border-top-color: #404040;
}

.footer-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 20px;
}

.tab-info {
	flex: 1;
	font-size: 13px;
	color: #666;
	transition: color 0.3s ease;
}

.product-modal.theme-dark .tab-info {
	color: #aaa;
}

.product-modal.theme-semi-dark .tab-info {
	color: #aaa;
}

.tab-info p {
	margin: 0;
	display: flex;
	align-items: center;
}

.text-gray-600 {
	color: #666;
	transition: color 0.3s ease;
}

.product-modal.theme-dark .text-gray-600 {
	color: #aaa;
}

.product-modal.theme-semi-dark .text-gray-600 {
	color: #aaa;
}

.action-buttons {
	display: flex;
	gap: 12px;
}

.action-buttons >>> .vs-button {
	min-width: 120px;
}

/* Utility Classes */
.mr-2 {
	margin-right: 8px;
}

.ml-1 {
	margin-left: 4px;
}

.mt-5 {
	margin-top: 24px;
}

.text-sm {
	font-size: 13px;
}

.text-gray-600 {
	color: #666;
}

/* Responsive */
@media (max-width: 768px) {
	.product-modal {
		width: 95%;
		height: 95vh;
	}

	.modal-header {
		padding: 16px 20px;
	}

	.modal-title {
		font-size: 20px;
	}

	.product-meta {
		gap: 12px;
	}

	.tabs-navigation {
		width: 120px;
		padding: 8px 0;
	}

	.tab-button {
		padding: 10px 12px;
		font-size: 12px;
	}

	.tabs-content {
		padding: 20px;
	}

	.overview-grid {
		grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
		gap: 12px;
	}

	.modal-footer {
		padding: 12px 20px;
	}

	.footer-content {
		flex-direction: column;
		align-items: flex-start;
	}

	.action-buttons {
		width: 100%;
	}

	.action-buttons >>> .vs-button {
		flex: 1;
	}
}

@media (max-width: 480px) {
	.product-modal {
		width: 98%;
		height: 98vh;
		border-radius: 8px;
	}

	.modal-header {
		flex-direction: column;
		gap: 12px;
	}

	.modal-title {
		font-size: 18px;
	}

	.close-btn {
		align-self: flex-end;
	}

	.product-meta {
		flex-direction: column;
		gap: 8px;
	}

	.tabs-navigation {
		width: 100px;
	}

	.overview-grid {
		grid-template-columns: 1fr;
	}
}

/* Dark Mode Support for Input Labels in Modal */
body.theme-dark .product-modal .vs-con-input-label,
body.theme-semi-dark .product-modal .vs-con-input-label {
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .vs-input-label,
body.theme-semi-dark .product-modal .vs-input-label {
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .vs-input label,
body.theme-semi-dark .product-modal .vs-input label {
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .vs-input,
body.theme-semi-dark .product-modal .vs-input {
	--vs-input-label-color: #e0e0e0;
}

body.theme-dark .product-modal label,
body.theme-semi-dark .product-modal label {
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .vs-input input,
body.theme-semi-dark .product-modal .vs-input input {
	color: #e0e0e0 !important;
	background: rgba(255, 255, 255, 0.05) !important;
	border-color: #3a3a3a !important;
}

body.theme-dark .product-modal .vs-input input::placeholder,
body.theme-semi-dark .product-modal .vs-input input::placeholder {
	color: #888 !important;
}

body.theme-dark .product-modal .multiselect,
body.theme-semi-dark .product-modal .multiselect {
	background: rgba(255, 255, 255, 0.05) !important;
	border-color: #3a3a3a !important;
}

body.theme-dark .product-modal .multiselect__tags,
body.theme-semi-dark .product-modal .multiselect__tags {
	background: rgba(255, 255, 255, 0.05) !important;
	border-color: #3a3a3a !important;
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .multiselect__input,
body.theme-semi-dark .product-modal .multiselect__input {
	background: transparent !important;
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .multiselect__single,
body.theme-semi-dark .product-modal .multiselect__single {
	background: transparent !important;
	color: #e0e0e0 !important;
}

body.theme-dark .product-modal .multiselect__option--selected,
body.theme-semi-dark .product-modal .multiselect__option--selected {
	background: #667eea !important;
	color: white !important;
}

body.theme-dark .product-modal .multiselect__option--highlight,
body.theme-semi-dark .product-modal .multiselect__option--highlight {
	background: #667eea !important;
	color: white !important;
}

body.theme-dark .product-modal .multiselect__content-wrapper,
body.theme-semi-dark .product-modal .multiselect__content-wrapper {
	background: #2a2a2a !important;
	border-color: #3a3a3a !important;
}

body.theme-dark .product-modal .multiselect__option,
body.theme-semi-dark .product-modal .multiselect__option {
	color: #e0e0e0 !important;
}

/* Modal form inputs and textarea */
body.theme-dark .product-modal textarea,
body.theme-semi-dark .product-modal textarea {
	color: #e0e0e0 !important;
	background: rgba(255, 255, 255, 0.05) !important;
	border-color: #3a3a3a !important;
}

body.theme-dark .product-modal textarea::placeholder,
body.theme-semi-dark .product-modal textarea::placeholder {
	color: #888 !important;
}
</style>

